import {
	DisciplineEnum,
	getLegacyReferralOptions,
	LegacyReferralStatusEnum,
	unassignToLegacyReferralOptions,
	type LegacyReferralAssignmentViewModel,
	type LegacyReferralViewModel
} from '@generated';
import { UserMinus, UserPlus } from 'lucide-react';
import React, { useState } from 'react';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Prompt } from '~/shared/components/Prompt';
import { Button } from '~/shared/components/buttons/Button';
import { formatToShortDateAndTimeOrNull } from '~/util/date';
import { ReferralAssignmentModal } from './ReferralAssignmentModal';

interface RoleAssignment {
	label: string;
	disciplineId: number;
	assignedTo: (referral: LegacyReferralViewModel) => LegacyReferralAssignmentViewModel | undefined;
	isStart?: boolean;
}

const assignmentRows: RoleAssignment[] = [
	{ label: 'SN SOC', assignedTo: x => x.snStartAssign, isStart: true, disciplineId: DisciplineEnum.SkilledNursing },
	{ label: 'SN Primary Care', assignedTo: x => x.snPrimaryCareAssign, isStart: false, disciplineId: DisciplineEnum.SkilledNursing },
	{ label: 'PT', assignedTo: x => x.ptAssign, disciplineId: DisciplineEnum.PhysicalTherapy },
	{ label: 'OT', assignedTo: x => x.otAssign, disciplineId: DisciplineEnum.OccupationalTherapy },
	{ label: 'ST', assignedTo: x => x.stAssign, disciplineId: DisciplineEnum.SpeechTherapy },
	{ label: 'MSW', assignedTo: x => x.mswAssign, disciplineId: DisciplineEnum.MedicalSocialWorker },
	{ label: 'HHA', assignedTo: x => x.hhaAssign, disciplineId: DisciplineEnum.HomeHealthAide }
];

export function ReferralAssignments({ referral }: { referral: LegacyReferralViewModel }) {
	const unassign = useMutation({
		...unassignToLegacyReferralOptions,
		invalidateQueryKeys: [getLegacyReferralOptions(referral.id).queryKey, getSearchQueryKey('legacy-referral')]
	});
	const [assignedPrompt, setAssignedPrompt] = useState({
		display: false,
		disciplineId: 0,
		startOfCare: false
	});
	const [unassignedPrompt, setUnassignedPrompt] = useState({
		display: false,
		startOfCare: false
	});

	const closeAssignedPrompt = () => setAssignedPrompt({ display: false, disciplineId: 0, startOfCare: false });

	const sameSnClinicianInBothRoles = !!referral.snStartAssign && referral.snStartAssign.assignedToId === referral.snPrimaryCareAssign?.assignedToId;

	const unassignReferral = (disciplineId: number, startOfCare: boolean) => {
		if (disciplineId === DisciplineEnum.SkilledNursing && sameSnClinicianInBothRoles) {
			setUnassignedPrompt({ display: true, startOfCare });
			return;
		}
		unassign.mutate({
			referralId: referral.id,
			disciplineId,
			startOfCare,
			primaryCare: disciplineId === DisciplineEnum.SkilledNursing && !startOfCare
		});
	};

	const unassignRn = (startOfCare: boolean, primaryCare: boolean) => {
		unassign.mutate(
			{
				referralId: referral.id,
				disciplineId: DisciplineEnum.SkilledNursing,
				startOfCare,
				primaryCare
			},
			{
				onSettled: () => setUnassignedPrompt({ display: false, startOfCare: false })
			}
		);
	};

	return (
		<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
			{assignmentRows
				.filter(x => referral.disciplinesNeeded.includes(x.disciplineId))
				.map(assignment => {
					const assignedTo = assignment.assignedTo(referral);
					return (
						<div
							key={`${assignment.disciplineId}-${assignment.isStart ?? false}`}
							className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
						>
							<div>
								<p className="text-sm font-semibold text-gray-900">{assignment.label}</p>
								{assignedTo ? (
									<>
										<p className="mt-0.5 text-sm text-gray-600">{assignedTo.assignedToName}</p>
										<p className="text-xs text-gray-400">{formatToShortDateAndTimeOrNull(assignedTo.assignedOn)}</p>
									</>
								) : (
									<p className="mt-0.5 text-sm italic text-gray-400">No assignment</p>
								)}
							</div>

							{referral.statusId !== LegacyReferralStatusEnum.Ntfc &&
								(!assignedTo ? (
									<Button
										intent="success"
										appearance="outline"
										onClick={() =>
											setAssignedPrompt({
												display: true,
												disciplineId: assignment.disciplineId,
												startOfCare: assignment.isStart ?? false
											})
										}
										Icon={UserPlus}
										label="Assign"
									/>
								) : (
									<Button
										intent="destructive"
										appearance="outline"
										onClick={() => unassignReferral(assignment.disciplineId, assignment.isStart ?? false)}
										Icon={UserMinus}
										label="Unassign"
										isPending={unassign.isPending}
									/>
								))}
						</div>
					);
				})}
			{unassignedPrompt.display && (
				<Prompt intent="custom" title="Unassign" text="Unassign roles:">
					<Button intent="destructive" onClick={() => setUnassignedPrompt({ display: false, startOfCare: false })} label="Cancel" />
					<Button
						autoFocus
						onClick={() => unassignRn(unassignedPrompt.startOfCare, !unassignedPrompt.startOfCare)}
						label={unassignedPrompt.startOfCare ? 'Unassign from SOC Only' : 'Unassign from Primary Care Only'}
						Icon={UserMinus}
						isPending={unassign.isPending}
					/>
					<Button autoFocus onClick={() => unassignRn(true, true)} label="Both" Icon={UserMinus} isPending={unassign.isPending} />
				</Prompt>
			)}
			{assignedPrompt.display && (
				<ReferralAssignmentModal
					referralId={referral.id}
					disciplineId={assignedPrompt.disciplineId}
					startOfCare={assignedPrompt.startOfCare}
					onCancel={closeAssignedPrompt}
					onSuccess={closeAssignedPrompt}
				/>
			)}
		</div>
	);
}
